import { IconButton, Typography, Divider } from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ExpeditedOrdersList from "Components/ExpeditedTracking/ExpeditedOrdersList";
import GenericMapWithMarkers from "Components/shared/GenericMapWithMarkers";
import {
  HeaderRow,
  MapContainer,
  PageContainer,
  SidebarBox,
} from "./ExpeditedTracking.styles";
import getExpeditedOrders from "services/expeditedTracking";
import locationMarkerIcon from "assets/icons/location-marker.svg";

const ExpeditedTracking = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const mapRef = useRef(null);

  const handleMapLoad = (map) => {
    mapRef.current = map;
  };

  const panToOrder = (order) => {
    if (mapRef.current && order?.coordinates) {
      mapRef.current.panTo({
        lat: order.coordinates.latitude,
        lng: order.coordinates.longitude,
      });
      setSelectedOrder(order);
    }
  };

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        // Fetch expedited orders (AwaitingSelection) with nearby drivers from the backend.
        // Backend handles filtering by type and status, and querying nearby drivers per order.
        const response = await getExpeditedOrders(1, 50, 5);
        // response is { page: [...], totalRecords }
        const page = response?.page || response || [];
        // Normalize sender fields because backend may return camelCased names like `senderFullName` / `senderPhone`
        const normalized = (page || []).map((o) => {
          const senderName = o.senderName || o.senderFullName || o.SenderFullName || o.senderFullname || "";
          // Try known keys first, then fallback to any property name that contains 'phone' (case-insensitive)
          let senderPhone =
            o.senderPhone ||
            o.senderPhoneNumber ||
            o.SenderPhone ||
            o.SenderPhoneNumber ||
            o.phoneNumber ||
            o.PhoneNumber ||
            o.phone ||
            o.Phone ||
            "";

          if (!senderPhone) {
            const phoneEntry = Object.entries(o).find(
              ([key, val]) => /phone/i.test(key) && val
            );
            if (phoneEntry) senderPhone = phoneEntry[1];
          }

          return { ...o, senderName, senderPhone };
        });

        // Debug: if phone is missing for first item, log available keys to help identify the returned field
        if (normalized.length > 0 && !normalized[0].senderPhone) {
          // eslint-disable-next-line no-console
          console.debug("Expedited API first order keys:", Object.keys(page[0] || {}));
          // eslint-disable-next-line no-console
          console.debug("Expedited API first order full object:", JSON.stringify(page[0] || {}, null, 2));
        }

        setOrders(normalized);
        setSelectedOrder(normalized[0] || null);
      } catch (err) {
        // API failed; show empty state and log error (no mock data fallback)
        console.error("Failed to fetch expedited orders from API:", err);
        setOrders([]);
        setSelectedOrder(null);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, []);

  // Custom SVG markers - Larger and clearer
  // Use the same location marker icon as DriversMapLocations for order pins

  const driverMarkerSvg = `
    <svg width="56" height="56" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 56 56">
      <defs>
        <filter id="shadow2" x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.3"/>
        </filter>
      </defs>
      <!-- Drop shadow background -->
      <ellipse cx="28" cy="48" rx="16" ry="4" fill="rgba(0,0,0,0.1)"/>
      <!-- Main pin shape -->
      <path d="M 28 8 C 18 8 10 16 10 26 C 10 38 28 52 28 52 C 28 52 46 38 46 26 C 46 16 38 8 28 8 Z" fill="#4CAF50" filter="url(#shadow2)"/>
      <!-- Border -->
      <path d="M 28 8 C 18 8 10 16 10 26 C 10 38 28 52 28 52 C 28 52 46 38 46 26 C 46 16 38 8 28 8 Z" fill="none" stroke="#388E3C" stroke-width="1.5"/>
      <!-- Car icon inside -->
      <g transform="translate(28, 24)">
        <!-- Car body -->
        <rect x="-6" y="-2" width="12" height="6" fill="white" rx="1"/>
        <!-- Car roof/windows -->
        <rect x="-4" y="-4" width="8" height="2" fill="white" rx="0.5"/>
        <!-- Left wheel -->
        <circle cx="-4" cy="4" r="2" fill="white"/>
        <!-- Right wheel -->
        <circle cx="4" cy="4" r="2" fill="white"/>
        <!-- Door line -->
        <line x1="0" y1="-2" x2="0" y2="4" stroke="#4CAF50" stroke-width="0.5" opacity="0.6"/>
      </g>
    </svg>
  `;

  const markers = selectedOrder
    ? [
        {
          position: {
            lat: selectedOrder.coordinates.latitude,
            lng: selectedOrder.coordinates.longitude,
          },
          icon: locationMarkerIcon,
          title: selectedOrder.orderNumber,
        },
        ...selectedOrder.drivers.map((driver) => ({
          position: {
            lat: driver.coordinates.latitude,
            lng: driver.coordinates.longitude,
          },
          icon: `data:image/svg+xml;base64,${btoa(driverMarkerSvg)}`,
          title: driver.fullName,
        })),
      ]
    : [];

  const center = selectedOrder
    ? {
        lat: selectedOrder.coordinates.latitude,
        lng: selectedOrder.coordinates.longitude,
      }
    : { lat: 25.276987, lng: 55.292919 };

  return (
    <PageContainer>
      <SidebarBox>
        <HeaderRow>
          <IconButton onClick={() => navigate(-1)} sx={{ padding: 0 }}>
            <ArrowBack fontSize="medium" />
          </IconButton>
          <Typography variant="h2" fontWeight="bold" color="text.primary">
            Active Expedited Orders
          </Typography>
        </HeaderRow>

        <Divider />

        <ExpeditedOrdersList
          orders={orders}
          selectedOrderId={selectedOrder?.orderId}
          showHeader={false}
          onSelect={(orderId) => {
            const order = orders.find((o) => o.orderId === orderId);
            if (order) panToOrder(order);
          }}
        />
      </SidebarBox>

      <MapContainer>
        <GenericMapWithMarkers
          center={center}
          markers={markers}
          zoom={14}
          onMapLoad={handleMapLoad}
        />
      </MapContainer>
    </PageContainer>
  );
};

export default ExpeditedTracking;
